import React, { useState, useEffect } from 'react';
import { Calendar, ExternalLink, Share2, Eye, } from 'lucide-react';
import ReactGA from 'react-ga4';
import NewsGrid from './NewsGrid';
import Header from './Header';
import Footer from './Footer';
import Loader from './Loader';
import { Archive } from 'lucide-react';
import QRWithForm from '../screens/QRWithForm';


// Menú discreto para herramientas adicionales
const ToolMenu = ({ isDarkMode }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="fixed top-4 ">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`p-3 rounded-full shadow-lg ${isDarkMode ? 'bg-gray-700 text-white' : 'bg-gray-200 text-gray-800'}`}
        title="Herramientas"
      >
        ⚙️
      </button>
      {isOpen && (
        <div className={`mt-2 rounded shadow-lg p-4 ${isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'}`}>
          <ul className="space-y-2">
            <li>
              <a href="/qr-generator" className="hover:underline">
                Generador de QR
              </a>
            </li>|
          </ul>
        </div>
      )}
    </div>
  );
};
// Configuración de Google Analytics (script será insertado en el componente principal)
const initializeGA = () => {
  if (typeof window !== 'undefined') {
    // Este código sería el equivalente de cómo se implementaría Google Analytics
    console.log('Google Analytics inicializado');
  }
};

// Configuración para AWS S3
const S3_CONFIG = {
  apiEndpoint: process.env.REACT_APP_API_URL
};


const NewsDashboard = () => {
  const [newsData, setNewsData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [adds, setAdds] = useState(false);
  const [archive, setArchive] = useState(true);
  const [error, setError] = useState(null);
  const [activeCategory, setActiveCategory] = useState('Todas');
  const [searchTerm, setSearchTerm] = useState('');
  const [isDarkMode, setIsDarkMode] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [dateFilter, setDateFilter] = useState({
    startDate: null,
    endDate: null
  });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showDateMenu, setShowDateMenu] = useState(false);
  const [archivedDates, setArchivedDates] = useState([]);
  const [archiveDateSelected, setArchiveDateSelected] = useState("");
  // Función para obtener noticias desde el bucket S3 a través de API Gateway/Lambda
  const fetchNewsFromJSONFile = async () => {
    setIsLoading(true);
    try {
      // Obtener noticias desde S3 a través de una API (construida con API Gateway + Lambda)
      const response = await fetch(`${S3_CONFIG.apiEndpoint}`);

      if (!response.ok) {
        throw new Error('Error al cargar noticias');
      }

      const data = await response.json();

      setNewsData(data || []);
      setTotalPages(data.totalPages || 1);
      setIsLoading(false);

    } catch (err) {
      console.error('Error fetching news from S3:', err);
      setError('No se pudieron cargar las noticias. Usando datos de respaldo.');
      setNewsData([]);
      setIsLoading(false);
    }
    setIsLoading(false);

  };


  const fetchArchivedDates = async () => {
    let result = [];
    try {
      // Aquí podrías hacer una llamada a tu API para obtener las fechas archivadas
      const response = await fetch(`${S3_CONFIG.apiEndpoint}archive`);

      if (!response.ok) {
        throw new Error('Error al cargar fechas archivadas');
      }

      result = await response.json();

    } catch (err) {
      console.error('Error fetching archived dates:', err);
    }
    return result;

  }

  // Cargar datos al iniciar y cuando cambien los filtros
  useEffect(() => {
    if (!showArchived)
      fetchNewsFromJSONFile();

    // Inicializar Google Analytics
    initializeGA();
  }, [page, showArchived]);

  // Categorías únicas para el filtro
  const categories = ['Todas', ...Array.from(new Set(newsData.map(item => item.category))).sort()];

  // Función para aplicar todos los filtros
  const applyFilters = () => {
    let filtered = newsData;

    // Filtrar por categoría
    if (activeCategory !== 'Todas') {
      filtered = filtered.filter(item => item.category === activeCategory);
    }

    // Filtrar por término de búsqueda
    if (searchTerm) {
      filtered = filtered.filter(item =>
        item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.resume.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    // Filtrar por fecha de inicio
    if (dateFilter.startDate && showArchived) {
      filtered = filtered.filter(item => new Date(item.date) >= new Date(dateFilter.startDate));
    }

    // Filtrar por fecha de fin
    if (dateFilter.endDate && showArchived) {
      filtered = filtered.filter(item => new Date(item.date) <= new Date(dateFilter.endDate));
    }
    /**
    // Filtrar por archivado/no archivado (si es necesario)
    filtered = filtered.filter(item => item.archived === showArchived);
    **/
    return filtered;
  };

  // Aplicar todos los filtros
  let filteredNews = applyFilters();

  // Toggle para modo oscuro
  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
    document.body.classList.toggle('dark-mode');
  };

  // Toggle para mostrar/ocultar noticias archivadas
  const toggleArchivedNews = () => {
    setShowArchived(!showArchived);
    fetchArchivedDates().then(dates => {
      console.log('Fechas archivadas:', dates);
      setArchivedDates(dates);
    }
    ).catch(err => {
      console.error('Error fetching archived dates:', err);
    }
    );

  };

  // Función para cargar más noticias (paginación)
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleDateSelect = async (startDate) => {
    setIsLoading(true);
    console.log('Fecha seleccionada:', startDate);
    setArchiveDateSelected(startDate);
    let result = [];
    try {
      // Aquí podrías hacer una llamada a tu API para obtener las fechas archivadas
      const response = await fetch(`${S3_CONFIG.apiEndpoint}archive/files?internal_id=${startDate}`);

      if (!response.ok) {
        throw new Error('Error al cargar fechas archivadas');
      }

      result = await response.json();
      setNewsData(result || []);

    } catch (err) {
      console.error('Error fetching archived dates:', err);

    }
    setIsLoading(false);

    return result;
  };

  // Componente de vista previa de página personalizado
  const PagePreview = () => (
    <div className="w-full h-full relative">
      {/* Simulación de barra de navegación */}
      <div className={`absolute top-0 left-0 right-0 h-6 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'} flex items-center px-2`}>
        <div className="flex space-x-1">
          <div className="w-2 h-2 rounded-full bg-red-500"></div>
          <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
          <div className="w-2 h-2 rounded-full bg-green-500"></div>
        </div>
        <div className={`mx-auto rounded-sm h-3 w-32 ${isDarkMode ? 'bg-gray-600' : 'bg-gray-300'}`}></div>
      </div>

      {/* Contenido principal */}
      <div className={`pt-8 px-4 ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'} h-full`}>
        {/* Header */}
        <div className={`h-4 w-3/4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-300'} mb-2`}></div>
        <div className={`h-3 w-1/2 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-300'} mb-4`}></div>

        {/* Imagen destacada */}
        <div className={`h-16 w-full rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-300'} mb-3`}></div>

        {/* Contenido de texto */}
        <div className="space-y-2">
          <div className={`h-2 w-full rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-300'}`}></div>
          <div className={`h-2 w-5/6 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-300'}`}></div>
          <div className={`h-2 w-full rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-300'}`}></div>
          <div className={`h-2 w-3/4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-300'}`}></div>
        </div>
      </div>
    </div>
  );

  const FeaturedPagePreview = () => (
    <div className="w-full h-full relative">
      {/* Barra de navegación */}
      <div className={`absolute top-0 left-0 right-0 h-8 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'} flex items-center px-3`}>
        <div className="flex space-x-2">
          <div className="w-3 h-3 rounded-full bg-red-500"></div>
          <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
          <div className="w-3 h-3 rounded-full bg-green-500"></div>
        </div>
        <div className={`mx-auto rounded px-2 py-1 h-5 w-48 ${isDarkMode ? 'bg-gray-600' : 'bg-gray-300'}`}></div>
        <div className={`h-5 w-5 rounded-sm ${isDarkMode ? 'bg-gray-600' : 'bg-gray-300'}`}></div>
      </div>

      {/* Contenido principal */}
      <div className={`pt-10 px-4 ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'} h-full`}>
        {/* Header y logo */}
        <div className="flex justify-between items-center mb-3">
          <div className={`h-6 w-24 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-300'}`}></div>
          <div className="flex space-x-1">
            <div className={`h-4 w-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-300'}`}></div>
            <div className={`h-4 w-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-300'}`}></div>
            <div className={`h-4 w-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-300'}`}></div>
          </div>
        </div>

        {/* Titular grande */}
        <div className={`h-7 w-5/6 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-300'} mb-3`}></div>

        {/* Imagen destacada */}
        <div className={`h-24 w-full rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-300'} mb-4`}></div>

        {/* Párrafos de texto */}
        <div className="space-y-2">
          <div className={`h-3 w-full rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-300'}`}></div>
          <div className={`h-3 w-11/12 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-300'}`}></div>
          <div className={`h-3 w-full rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-300'}`}></div>
          <div className={`h-3 w-4/5 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-300'}`}></div>
        </div>

        {/* Indicadores sociales */}
        <div className="flex justify-between mt-4">
          <div className="flex space-x-2">
            <div className={`h-4 w-12 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-300'}`}></div>
            <div className={`h-4 w-12 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-300'}`}></div>
          </div>
          <div className={`h-4 w-16 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-300'}`}></div>
        </div>
      </div>
    </div>
  );


  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-800'}`}>
      <Header
        isDarkMode={isDarkMode}
        setSearchTerm={setSearchTerm}
        searchTerm={searchTerm}
        toggleDarkMode={toggleDarkMode}
        toggleArchivedNews={toggleArchivedNews}
        // Show elements         
        darkMode={true}
        search={true}
      />

      <ToolMenu isDarkMode={isDarkMode} />

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">

        <Loader
          isDarkMode={isDarkMode}
          isLoading={isLoading}
        />


        {/* Título de la sección */}
        <div className="mb-6 flex justify-between items-center">
          <h2 className="text-2xl font-bold">
            {showArchived ? "Archivo Histórico de Noticias ("+archiveDateSelected+")" : "Noticias Actuales"}
          </h2>

          <div className="mb-6 flex justify-between items-center">
            <button
              onClick={() => {
                toggleArchivedNews();
                setShowDateMenu(true);
              }}
              className={`p-2 rounded-full ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'} ${showArchived ? 'text-blue-500' : ''}`}
              title={showArchived ? "Ver noticias actuales" : "Ver archivo histórico"}
            >

              <Archive className="w-5 h-5" ></Archive>
            </button>

            {showArchived && showDateMenu && (
              <div className={` mt-2 rounded shadow-lg z-10 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                <ul>

                  {archivedDates.map(date => {
                    return (
                      <li className="cursor-pointer text-blue-500 hover:text-blue-700 text-sm" onClick={() => handleDateSelect(date)}>{date}</li>
                    )
                  })}

                </ul>
              </div>
            )}
          </div>

        </div>



        {/* Category Filter */}
        <div className="mb-8 flex overflow-x-auto pb-2 scrollbar-hide">
          <div className="flex space-x-2">
            {categories.map(category => (
              <button
                key={category}
                onClick={() => {
                  ReactGA.event({
                    category: 'Usuario',
                    action: 'Click en categoría',
                    label: category
                  });
                  setActiveCategory(category)
                }}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap ${activeCategory === category
                  ? 'bg-blue-500 text-white'
                  : isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'
                  }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>


        <NewsGrid
          filteredNews={filteredNews}
          isDarkMode={isDarkMode}
          PagePreview={PagePreview}
          Calendar={Calendar}
          ExternalLink={ExternalLink}
          Share2={Share2}
          Eye={Eye}
          FeaturedPagePreview={FeaturedPagePreview}
        />


      </main>

      <Footer
        isDarkMode={isDarkMode}
        setSearchTerm={setSearchTerm}
        searchTerm={searchTerm}
        toggleDarkMode={toggleDarkMode}
        toggleArchivedNews={toggleArchivedNews}
        showArchived={showArchived}
        // Show elements
        archive={archive}
        darkMode={true}
        search={false}
        showArchiveButton={false}
        categories={categories}
        setActiveCategory={setActiveCategory}
      />


    </div>
  );
};

export default NewsDashboard;